// React
import React, { useCallback, useMemo, useRef, useState } from "react";

// MUI
import { Menu, useMediaQuery } from "@mui/material";

// Third party imports
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// Redux
import { setSnackbar } from "redux/slices/snackbarSlice";

// Tokens
import { zIndex } from "styles/tokens";

import { ContextMenuActionsContext, ContextMenuStateContext } from "./context";
// Relative imports
import { renderEntries, useSubmenuLevel } from "./MenuEntries";
import {
  isKeyboardContextMenu,
  normalizeItems,
  pointFromElement,
  shouldUseNativeMenu,
} from "./utils";

// A native contextmenu that follows a keyboard or long-press opening by less than this is the
// same gesture: it must not reopen nor close the menu
const DUPLICATE_WINDOW_MS = 500;
const LONG_PRESS_MS = 500;
const LONG_PRESS_TOLERANCE_PX = 8;

/**
 * Hosts the one context menu of the app (a single MUI Menu). Mount it once, inside the Router,
 * Redux and ThemeProvider (App.jsx does). Components use it through useContextMenu() and
 * <ActionMenuButton />: see ./README.md.
 */
export default function ContextMenuProvider({ children }) {
  const { t, i18n } = useTranslation("ui");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const [menu, setMenu] = useState(null); // { anchor, items, options, target, event }
  const [open, setOpen] = useState(false);
  const lastOpenRef = useRef({ at: 0, source: null });
  const longPressRef = useRef(null); // { timer, x, y, fired }
  const level = useSubmenuLevel();
  const { closeSub } = level;

  // Latest values read by the stable callbacks below
  const latestRef = useRef({});
  latestRef.current = { dispatch, navigate, t, i18n, menu };

  const close = useCallback(() => {
    closeSub();
    setOpen(false);
  }, [closeSub]);

  // anchor: { kind: "position", x, y } | { kind: "element", el }
  const show = useCallback(
    (anchor, rawItems, options = {}, meta = {}) => {
      const items = normalizeItems(rawItems);
      if (!items.length) return false;
      lastOpenRef.current = { at: Date.now(), source: meta.source || "api" };
      closeSub();
      setMenu({ anchor, items, options, target: meta.target || null, event: meta.event || null });
      setOpen(true);
      return true;
    },
    [closeSub]
  );

  /**
   * Opens the menu at a point or where an event happened.
   * @param {{x:number,y:number} | Event | React.SyntheticEvent} where
   * @param {Array|Function} items menu items (see README), or a function returning them
   * @param {object} [options] { data, ariaLabel, onClose }
   * @returns {boolean} whether a menu opened (false when every item was hidden)
   */
  const openAt = useCallback(
    (where, items, options) => {
      if (!where) return false;
      const isEvent = typeof where.preventDefault === "function";
      let point = { x: where.x, y: where.y };
      if (isEvent) {
        const element = where.currentTarget instanceof Element ? where.currentTarget : where.target;
        point = isKeyboardContextMenu(where)
          ? pointFromElement(element)
          : { x: where.clientX, y: where.clientY };
      }
      const opened = show({ kind: "position", ...point }, items, options, {
        source: isEvent && isKeyboardContextMenu(where) ? "keyboard" : "pointer",
        target: isEvent ? where.currentTarget || where.target : null,
        event: isEvent ? where : null,
      });
      if (opened && isEvent) where.preventDefault();
      return opened;
    },
    [show]
  );

  /**
   * Opens the menu under an element (the ⋮ button), aligned to its right edge.
   * @param {Element} anchorEl
   * @param {Array|Function} items
   * @param {object} [options] { data, ariaLabel, onClose, align: "right" | "left" }
   */
  const openFrom = useCallback(
    (anchorEl, items, options) =>
      show({ kind: "element", el: anchorEl }, items, options, {
        source: "button",
        target: anchorEl,
      }),
    [show]
  );

  /**
   * Props to spread on an element so it opens the menu on right-click, the ContextMenu key,
   * Shift+F10 and, with { longPress: true }, a long touch.
   * @param {(event) => Array} getItems called when the menu opens
   * @param {{ longPress?: boolean, disabled?: boolean, options?: object }} [bindOptions]
   */
  const bind = useCallback(
    (getItems, bindOptions = {}) => {
      const { longPress = false, disabled = false, options } = bindOptions;
      if (disabled) return {};
      const itemsFor = (event) => (typeof getItems === "function" ? getItems(event) : getItems);

      const props = {
        onContextMenu: (event) => {
          if (event.defaultPrevented || shouldUseNativeMenu(event)) return;
          const last = lastOpenRef.current;
          if (last.source !== "pointer" && Date.now() - last.at < DUPLICATE_WINDOW_MS) {
            // second event of a keyboard or long-press opening
            event.preventDefault();
            return;
          }
          openAt(event, itemsFor(event), options);
        },
        onKeyDown: (event) => {
          if (event.defaultPrevented) return;
          const isMenuKey = event.key === "ContextMenu" || (event.shiftKey && event.key === "F10");
          if (!isMenuKey || shouldUseNativeMenu(event)) return;
          const element = event.currentTarget;
          const opened = show(
            { kind: "position", ...pointFromElement(element) },
            itemsFor(event),
            options,
            {
              source: "keyboard",
              target: element,
              event,
            }
          );
          if (opened) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
      };

      if (longPress) {
        const cancel = () => {
          clearTimeout(longPressRef.current?.timer);
          longPressRef.current = null;
        };
        props.onPointerDown = (event) => {
          if (event.pointerType !== "touch") return;
          cancel();
          const { clientX: x, clientY: y } = event;
          const element = event.currentTarget;
          const timer = setTimeout(() => {
            longPressRef.current = { fired: Date.now() };
            show({ kind: "position", x, y }, itemsFor(event), options, {
              source: "longpress",
              target: element,
              event,
            });
          }, LONG_PRESS_MS);
          longPressRef.current = { timer, x, y };
        };
        props.onPointerMove = (event) => {
          const press = longPressRef.current;
          if (!press?.timer) return;
          if (
            Math.abs(event.clientX - press.x) > LONG_PRESS_TOLERANCE_PX ||
            Math.abs(event.clientY - press.y) > LONG_PRESS_TOLERANCE_PX
          ) {
            cancel();
          }
        };
        props.onPointerUp = () => {
          if (longPressRef.current?.timer) cancel();
        };
        props.onPointerCancel = cancel;
        // The tap that ends a long press must not also open the element
        props.onClickCapture = (event) => {
          const fired = longPressRef.current?.fired;
          if (fired && Date.now() - fired < DUPLICATE_WINDOW_MS * 2) {
            event.preventDefault();
            event.stopPropagation();
          }
          if (fired) longPressRef.current = null;
        };
      }
      return props;
    },
    [openAt, show]
  );

  const actions = useMemo(
    () => ({ openAt, openFrom, close, bind }),
    [openAt, openFrom, close, bind]
  );

  // Runs an item: the menu closes first, async handlers run after (errors become a snackbar)
  const runItem = useCallback(
    (item, event) => {
      const { menu: current, dispatch: d, navigate: nav, i18n: i18nInstance } = latestRef.current;
      close();
      if (typeof item.onClick !== "function") return;
      const ctx = {
        item,
        event,
        close,
        data: current?.options?.data,
        target: current?.target,
        triggerEvent: current?.event,
        dispatch: d,
        navigate: nav,
        t: i18nInstance.t.bind(i18nInstance),
      };
      const fail = (error) => {
        console.error("Context menu action failed:", error);
        d(
          setSnackbar({
            open: true,
            message: i18nInstance.t("ui:contextMenu.actionFailed"),
            severity: "error",
          })
        );
      };
      try {
        Promise.resolve(item.onClick(ctx)).catch(fail);
      } catch (error) {
        fail(error);
      }
    },
    [close]
  );

  // A right-click on the backdrop closes the menu instead of showing the browser's one
  const handleRootContextMenu = useCallback(
    (event) => {
      event.preventDefault();
      // Only the backdrop: a right-click on an item (or in a submenu, a React child of the
      // root through its portal) must not throw the menu away
      const target = event.target;
      const onBackdrop =
        target === event.currentTarget || target?.classList?.contains("MuiBackdrop-root");
      if (!onBackdrop) return;
      if (Date.now() - lastOpenRef.current.at < DUPLICATE_WINDOW_MS) return;
      close();
    },
    [close]
  );

  const handleClose = useCallback(() => {
    close();
    latestRef.current.menu?.options?.onClose?.();
  }, [close]);

  const anchor = menu?.anchor;
  const isElementAnchor = anchor?.kind === "element";
  const align = menu?.options?.align === "left" ? "left" : "right";
  const anchorEl = isElementAnchor ? anchor.el : null;
  const state = useMemo(() => ({ open, anchorEl }), [open, anchorEl]);

  return (
    <ContextMenuActionsContext.Provider value={actions}>
      <ContextMenuStateContext.Provider value={state}>
        {children}
        {menu && (
          <Menu
            open={open && (!isElementAnchor || !!anchorEl?.isConnected)}
            onClose={handleClose}
            anchorReference={isElementAnchor ? "anchorEl" : "anchorPosition"}
            anchorEl={anchorEl}
            anchorPosition={isElementAnchor ? undefined : { top: anchor.y, left: anchor.x }}
            anchorOrigin={isElementAnchor ? { vertical: "bottom", horizontal: align } : undefined}
            transformOrigin={isElementAnchor ? { vertical: "top", horizontal: align } : undefined}
            transitionDuration={reduceMotion ? 0 : "auto"}
            // submenus live in their own Popper outside the menu: focus must be able to reach them
            disableEnforceFocus
            variant="menu"
            // above modal.jsx (1301) and the dialogs opened from it (1350)
            sx={{ zIndex: zIndex.popover }}
            slotProps={{
              root: { onContextMenu: handleRootContextMenu },
              list: {
                dense: true,
                "aria-label": menu.options?.ariaLabel || t("contextMenu.ariaLabel"),
              },
              paper: { sx: { minWidth: 220, maxWidth: 320 } },
              transition: { onExited: () => setMenu(null) },
            }}
          >
            {renderEntries(menu.items, level, runItem)}
          </Menu>
        )}
      </ContextMenuStateContext.Provider>
    </ContextMenuActionsContext.Provider>
  );
}
